import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    IconChevronDown,
    IconDownload,
    IconHistory,
    IconEye,
    IconPlus,
    IconPencil,
    IconTrash,
    IconMessage,
    IconReceipt,
    IconCircleCheck,
    IconMinus,
} from '@tabler/icons-react';
import { Button, Table, Text } from '@fragments';
import Popper from '@fragments/Popper';
import { CommunityBanner } from '@components/Community';
import { getIntlLocale } from '@i18n';
import './styles.scss';

// Labels are resolved at render so they follow the active language; the map key
// is the API log type and must never be translated.
const getLogTypeMap = (t) => ({
    // Community
    'community.created': { label: t('logs.types.communityCreated'),       icon: <IconPlus size={13} /> },
    'community.edited':  { label: t('logs.types.communityEdited'),        icon: <IconPencil size={13} /> },
    'community.deleted': { label: t('logs.types.communityDeleted'),       icon: <IconTrash size={13} /> },
    // Content
    'content.created':   { label: t('logs.types.contentCreated'),         icon: <IconPlus size={13} /> },
    'content.updated':   { label: t('logs.types.contentUpdated'),         icon: <IconPencil size={13} /> },
    'content.deleted':   { label: t('logs.types.contentDeleted'),         icon: <IconTrash size={13} /> },
    'content.viewed':    { label: t('logs.types.contentViewed'),          icon: <IconEye size={13} /> },
    // Content comment
    'content_comment.created': { label: t('logs.types.contentCommentCreated'), icon: <IconMessage size={13} /> },
    'content_comment.updated': { label: t('logs.types.contentCommentUpdated'), icon: <IconPencil size={13} /> },
    'content_comment.deleted': { label: t('logs.types.contentCommentDeleted'), icon: <IconTrash size={13} /> },
    // Playlist
    'playlist.created': { label: t('logs.types.playlistCreated'),         icon: <IconPlus size={13} /> },
    'playlist.updated': { label: t('logs.types.playlistUpdated'),         icon: <IconPencil size={13} /> },
    'playlist.deleted': { label: t('logs.types.playlistDeleted'),         icon: <IconTrash size={13} /> },
    // Section
    'section.created':  { label: t('logs.types.sectionCreated'),          icon: <IconPlus size={13} /> },
    'section.updated':  { label: t('logs.types.sectionUpdated'),          icon: <IconPencil size={13} /> },
    'section.deleted':  { label: t('logs.types.sectionDeleted'),          icon: <IconTrash size={13} /> },
    // Subscription
    'subscription.created':  { label: t('logs.types.subscriptionCreated'),   icon: <IconReceipt size={13} /> },
    'subscription.paid':     { label: t('logs.types.subscriptionPaid'),      icon: <IconCircleCheck size={13} /> },
    'subscription.canceled': { label: t('logs.types.subscriptionCanceled'),  icon: <IconTrash size={13} /> },
    // Withdrawal
    'withdrawal.requested':  { label: t('logs.types.withdrawalRequested'),   icon: <IconReceipt size={13} /> },
    'withdrawal.configured': { label: t('logs.types.withdrawalConfigured'),  icon: <IconPencil size={13} /> },
    // Charge
    'charge.refunded': { label: t('logs.types.chargeRefunded'), icon: <IconPencil size={13} /> },
    'charge.paid':     { label: t('logs.types.chargePaid'),     icon: <IconCircleCheck size={13} /> },
    // Group
    'group.created': { label: t('logs.types.groupCreated'),     icon: <IconPlus size={13} /> },
    'group.updated': { label: t('logs.types.groupUpdated'),     icon: <IconPencil size={13} /> },
    'group.deleted': { label: t('logs.types.groupDeleted'),     icon: <IconTrash size={13} /> },
    // Post
    'post.created': { label: t('logs.types.postCreated'),       icon: <IconPlus size={13} /> },
    'post.updated': { label: t('logs.types.postUpdated'),       icon: <IconPencil size={13} /> },
    'post.deleted': { label: t('logs.types.postDeleted'),       icon: <IconTrash size={13} /> },
    // Material
    'material.created':     { label: t('logs.types.materialCreated'),     icon: <IconPlus size={13} /> },
    'material.updated':     { label: t('logs.types.materialUpdated'),     icon: <IconPencil size={13} /> },
    'material.deleted':     { label: t('logs.types.materialDeleted'),     icon: <IconTrash size={13} /> },
    'material.downloaded':  { label: t('logs.types.materialDownloaded'),  icon: <IconDownload size={13} /> },
    'material.viewed':      { label: t('logs.types.materialViewed'),      icon: <IconEye size={13} /> },
});

function formatDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString(getIntlLocale(), {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

const getColumns = (t) => {
    const logTypes = getLogTypeMap(t);

    return [
        {
            id: 'type',
            title: t('logs.table.action'),
            minWidth: 180,
            customRender: (item) => {
                const { label, icon } = logTypes[item.type] ?? { label: item.type, icon: <IconMinus size={13} /> };
                return (
                    <span className="log-type-badge">
                        {icon}
                        {label}
                    </span>
                );
            },
        },
        {
            id: 'details',
            title: t('logs.table.details'),
            grow: 1,
            customRender: (item) => {
                const { label, material_id, ...rest } = item.details ?? {};
                const subtitle = material_id ?? Object.values(rest)[0] ?? null;
                return (
                    <div className="log-detail-label">
                        {label
                            ? <Text.span contrast={7}>{label}</Text.span>
                            : <Text.span contrast={4}>—</Text.span>
                        }
                        {subtitle && !label && (
                            <span className="log-detail-id">{subtitle}</span>
                        )}
                        {subtitle && label && (
                            <span className="log-detail-id">{subtitle}</span>
                        )}
                    </div>
                );
            },
        },
        {
            id: 'created',
            title: t('common:fields.date'),
            minWidth: 160,
            render: (item) => formatDate(item.timestamps?.created),
        },
    ];
};

export default function UserLogsView({
    adminCommunities,
    selectedCommunity,
    logs,
    loading,
    pagination,
    fetchLogs,
    exportLogs,
    popperRef,
    handleSelectCommunity,
    user,
}) {
    const { t } = useTranslation('member');
    const columns = useMemo(() => getColumns(t), [t]);

    function openCommunityPopper(e) {
        const options = adminCommunities.map((c) => ({
            id: c.id,
            label: c.name,
            variant: c.id === selectedCommunity?.id ? 'active' : 'default',
            icon: <CommunityBanner small banner={c.banner} />,
            onClick: () => handleSelectCommunity(c),
        }));
        popperRef.current.open(options, 'bottom-start', e.currentTarget);
    }

    return (
        <div className="user-logs mt-5">
            <Text.h3 left={<IconHistory size={22} />} className="mb-1">
                {t('logs.title')}
            </Text.h3>
            <Text.h5 className="mb-3" contrast={5} weight={300}>
                {t('logs.subtitle', { name: user?.username?.split(' ')[0] ?? '' })}
            </Text.h5>

            <div className="user-logs-filters mb-3">
                <div>
                    <Text.span>
                        {t('common:entities.community')}
                    </Text.span>
                    <Popper
                        controlRef={popperRef}
                        config={{ searchKeyExtractor: (opt, validate) => validate(opt.label) }}
                    >
                        <Button
                            variant="dashed"
                            size="medium"
                            color="overlay"
                            className="mt-1"
                            startIcon={<CommunityBanner size={20} banner={selectedCommunity?.banner} />}
                            endIcon={<IconChevronDown size={14} />}
                            text={selectedCommunity?.name ?? t('logs.selectCommunity')}
                            onClick={openCommunityPopper}
                        />
                    </Popper>
                </div>
                <Button
                    variant="dashed"
                    size="small"
                    startIcon={<IconDownload size={15} />}
                    text={t('logs.export')}
                    color="inverse"
                    disabled={!selectedCommunity || logs?.length === 0}
                    onClick={exportLogs}
                />
            </div>

            <Table
                id="user-logs-table"
                columns={columns}
                data={logs}
                loading={loading}
                loadingRows={6}
                contained
                enablePagination
                pages={pagination.pages}
                records={pagination.records}
                page={pagination.page}
                onSelectPage={fetchLogs}
                emptyMessage={t('logs.empty')}
                emptySubtitle={t('logs.emptySubtitle')}
            />
        </div>
    );
}
